import React, { useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faDownload } from "@fortawesome/free-solid-svg-icons";
import { listEventRsvps } from "../../utils/events";

/**
 * EventRsvpModal — owner-facing list of attendees who RSVPed to an event.
 *
 * Props:
 *   event   — the event row (must include `id` and `title`).
 *   onClose — callback to close the modal.
 */
const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return "";
  const s = String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const slugify = (str) =>
  String(str || "event")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 60) || "event";

export default function EventRsvpModal({ event, onClose }) {
  const [rsvps, setRsvps] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [capacity, setCapacity] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const fetchPage = useCallback(
    async (after = null) => {
      try {
        const res = await listEventRsvps(event.id, after ? { cursor: after } : {});
        setRsvps((prev) => (after ? [...prev, ...(res?.rsvps || [])] : res?.rsvps || []));
        setCursor(res?.nextCursor || null);
        setCapacity(res?.capacity ?? null);
        setTotalCount(res?.totalRsvpCount ?? 0);
      } catch (err) {
        setError(err?.message || "Couldn't load RSVPs.");
      }
    },
    [event.id],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchPage(null).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const handleLoadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    await fetchPage(cursor);
    setLoadingMore(false);
  };

  const handleExportCsv = () => {
    const header = ["Name", "Email", "Status", "RSVP'd at"];
    const rows = rsvps.map((r) => [
      r.displayName || "",
      r.email || "",
      r.status || "",
      r.createdAt || "",
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    a.download = `rsvps-${slugify(event.title)}-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!event) return null;

  const summary =
    capacity != null
      ? `${totalCount} / ${capacity} reserved`
      : `${totalCount} reserved`;

  return (
    <div
      className="events-manager__rsvp-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="events-manager__rsvp-modal" role="dialog" aria-modal="true">
        <div className="events-manager__rsvp-header">
          <div>
            <span className="events-manager__rsvp-eyebrow">Event RSVPs</span>
            <h3 className="events-manager__rsvp-title">{event.title}</h3>
            <p className="events-manager__rsvp-subtitle">{summary}</p>
          </div>
          <button
            type="button"
            className="events-manager__rsvp-close"
            onClick={onClose}
            aria-label="Close RSVPs"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="events-manager__rsvp-body">
          {loading ? (
            <div className="events-manager__rsvp-empty">Loading RSVPs…</div>
          ) : error ? (
            <div className="events-manager__rsvp-empty" style={{ color: "#b91c1c" }}>
              {error}
            </div>
          ) : rsvps.length === 0 ? (
            <div className="events-manager__rsvp-empty">
              No RSVPs yet — share your event to drive interest.
            </div>
          ) : (
            <table className="events-manager__rsvp-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>RSVP'd at</th>
                </tr>
              </thead>
              <tbody>
                {rsvps.map((r) => (
                  <tr key={r.ticketId}>
                    <td>{r.displayName || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                    <td>
                      {r.email ? (
                        <a href={`mailto:${r.email}`}>{r.email}</a>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>—</span>
                      )}
                    </td>
                    <td>{formatDate(r.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="events-manager__rsvp-toolbar">
          {cursor && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
          <button
            type="button"
            className="btn-primary"
            onClick={handleExportCsv}
            disabled={rsvps.length === 0}
          >
            <FontAwesomeIcon icon={faDownload} /> Export CSV
          </button>
        </div>
      </div>
    </div>
  );
}
